<template>
  <transition name="overlay-fade">
    <div v-if="modelValue" class="manager-overlay" @click.self="close">
      <div class="manager-modal" role="dialog" aria-modal="true" aria-label="Gestion des technologies">
        <div class="manager-header">
          <h2>Technologies</h2>
          <button type="button" class="close-btn" title="Fermer" @click="close">&times;</button>
        </div>

        <form class="manager-create" @submit.prevent="submitCreate">
          <input
              v-model="newTechnologyName"
              type="text"
              placeholder="Nouvelle technologie"
          >
          <button class="accent-btn" type="submit" :disabled="creating" title="Créer une technologie">
            {{ creating ? '...' : 'Ajouter' }}
          </button>
        </form>

        <div class="manager-list">
          <div
              v-for="technology in localTechnologies"
              :key="technology.id"
              class="manager-row"
          >
            <input
                v-model="technology.name"
                type="text"
                class="manager-input"
            >

            <button
                type="button"
                class="ghost-btn"
                title="Enregistrer cette technologie"
                @click="saveTechnology(technology)"
            >
              Enregistrer
            </button>

            <button
                type="button"
                class="danger-btn"
                title="Supprimer cette technologie"
                @click="removeTechnology(technology.id)"
            >
              Supprimer
            </button>
          </div>
        </div>

        <p v-if="errorText" class="error-text">{{ errorText }}</p>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'TechnologyManagerModal',

  props: {
    creating: {
      type: Boolean,
      default: false
    },
    createTechnology: {
      type: Function,
      default: null
    },
    deleteTechnology: {
      type: Function,
      default: null
    },
    modelValue: {
      type: Boolean,
      default: false
    },
    technologies: {
      type: Array,
      default: () => []
    },
    updateTechnology: {
      type: Function,
      default: null
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      errorText: '',
      localTechnologies: [],
      newTechnologyName: ''
    };
  },

  watch: {
    modelValue(value) {
      if (value) {
        this.localTechnologies = JSON.parse(JSON.stringify(this.technologies || []));
        this.errorText = '';
        this.newTechnologyName = '';
      }
    },

    technologies: {
      deep: true,
      handler(nextTechnologies) {
        if (this.modelValue) {
          this.localTechnologies = JSON.parse(JSON.stringify(nextTechnologies || []));
        }
      }
    }
  },

  methods: {
    close() {
      this.$emit('update:modelValue', false);
    },

    async submitCreate() {
      const name = this.newTechnologyName.trim();
      if (!name || !this.createTechnology) {
        return;
      }

      this.errorText = '';

      try {
        await this.createTechnology(name);
        this.newTechnologyName = '';
      } catch (error) {
        this.errorText = error?.response?.data?.error || error?.response?.data?.detail || 'Création impossible';
      }
    },

    async saveTechnology(technology) {
      const name = String(technology?.name || '').trim();
      if (!name || !this.updateTechnology || !technology?.id) {
        return;
      }

      this.errorText = '';

      try {
        await this.updateTechnology(technology.id, name);
      } catch (error) {
        this.errorText = error?.response?.data?.error || error?.response?.data?.detail || 'Mise à jour impossible';
      }
    },

    async removeTechnology(technologyId) {
      if (!this.deleteTechnology) {
        return;
      }

      this.errorText = '';

      try {
        await this.deleteTechnology(technologyId);
      } catch (error) {
        this.errorText = error?.response?.data?.error || error?.response?.data?.detail || 'Suppression impossible';
      }
    }
  }
}
</script>

<style scoped>
.manager-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1120;
  padding: 28px 16px;
  box-sizing: border-box;
}

.manager-modal {
  width: min(820px, 100%);
  max-height: calc(100dvh - 56px);
  overflow: auto;
  background: #2a2b2e;
  border: 1px solid #444851;
  border-radius: 20px;
  box-shadow: 0 0 24px #00f0ff33;
  padding: 1rem;
}

.manager-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.manager-header h2 {
  margin: 0;
  color: #00f0ff;
}

.close-btn {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 1.8rem;
  cursor: pointer;
}

.manager-create {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.manager-create input,
.manager-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #50545d;
  border-radius: 14px;
  background: #252629;
  color: white;
  padding: 0.85rem 1rem;
}

.manager-list {
  display: grid;
  gap: 0.75rem;
}

.manager-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.75rem;
  align-items: center;
}

.accent-btn,
.ghost-btn,
.danger-btn {
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 0.75rem 1rem;
}

.accent-btn {
  background: rgba(0, 240, 255, 0.12);
  color: #00f0ff;
  border-color: #00f0ff;
}

.ghost-btn {
  background: #252629;
  color: white;
  border-color: #50545d;
}

.danger-btn {
  background: rgba(255, 0, 0, 0.12);
  color: #ff8080;
  border-color: #ff4d4d;
}

.error-text {
  color: #ff8a8a;
  margin: 1rem 0 0;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.22s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

@media (max-width: 720px) {
  .manager-create,
  .manager-row {
    grid-template-columns: 1fr;
  }
}
</style>
